import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const RegisterPage = () => {
    const { register } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [form, setForm] = useState({
        name: '',
        email: '',
        password: '',
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!form.name || !form.email || !form.password) {
            toast.error(t('pleaseSignIn'));
            return;
        }

        if (form.password.length < 6) {
            toast.error(t('atLeast6Chars'));
            return;
        }

        setLoading(true);
        try {
            await register(form.name, form.email, form.password);
            toast.success(t('accountCreated'));
            navigate('/');
        } catch (error) {
            toast.error(error.response?.data?.detail || t('registrationFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-transition min-h-[calc(100vh-5rem)] flex items-center" data-testid="register-page">
            <div className="max-w-[400px] mx-auto px-6 py-16 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div className="text-center mb-12">
                        <h1 className="font-display text-4xl font-light mb-2">{t('createAccount')}</h1>
                        <p className="text-stone-500">{t('joinCommunity')}</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <Label htmlFor="name" className="text-xs uppercase tracking-wider text-stone-500">
                                {t('fullName')}
                            </Label>
                            <Input
                                id="name"
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="mt-2 rounded-none border-stone-200 focus:border-fjord"
                                placeholder="Ola Nordmann"
                                data-testid="register-name-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-stone-500">
                                {t('email')}
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="mt-2 rounded-none border-stone-200 focus:border-fjord"
                                placeholder="din@epost.no"
                                data-testid="register-email-input"
                            />
                        </div>

                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-stone-500">
                                {t('password')}
                            </Label>
                            <div className="relative mt-2">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="rounded-none border-stone-200 focus:border-fjord pr-10"
                                    placeholder="••••••••"
                                    data-testid="register-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            <p className="text-xs text-stone-400 mt-2">
                                {t('atLeast6Chars')}
                            </p>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-fjord hover:bg-fjord-dark text-white rounded-none py-6 text-xs uppercase tracking-[0.15em] font-bold"
                            data-testid="register-submit-button"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {t('creatingAccount')}
                                </>
                            ) : (
                                t('createAccount')
                            )}
                        </Button>
                    </form>

                    <p className="text-center text-sm text-stone-500 mt-8">
                        {t('alreadyHaveAccount')}{' '}
                        <Link 
                            to="/login" 
                            className="text-fjord hover:underline"
                            data-testid="login-link"
                        >
                            {t('signIn')}
                        </Link>
                    </p>
                </motion.div>
            </div>
        </div>
    );
};

export default RegisterPage;
